import type { ReactNode } from "react"
import { SiteHeader } from "@/components/site-header"
import { SiteFooter } from "@/components/site-footer"
import { EditorialHero } from "./editorial-hero"
import { FAQAccordion } from "./faq-accordion"
import { RelatedToolsRail, type RelatedToolItem } from "./related-tools-rail"
import { CTABand } from "./cta-band"
import { SectionRule } from "./section-rule"
import { ToolProductCarousel } from "./tool-product-carousel"
import { ToolRecentlyViewedRail } from "./tool-recently-viewed-rail"
import { BreadcrumbJsonLd } from "@/components/seo/breadcrumb-jsonld"
import { toolBreadcrumbs, type BreadcrumbTrail } from "@/lib/breadcrumbs"

interface FAQItem {
  question: string
  answer: string
}

type TopicFamily =
  | "fridge"
  | "freezer"
  | "wine"
  | "beverage"
  | "ice"
  | "mini"
  | "rv"
  | "outdoor"
  | "commercial"
  | "filter"
  | "kitchen-helper"

interface BonusCarouselConfig {
  primary: { eyebrow: string; title: string; description: string; collectionType: string; fallbackCollectionType?: string }
  secondary: { eyebrow: string; title: string; description: string; collectionType: string; fallbackCollectionType?: string }
}

/**
 * Topic-family → bonus-carousel pair. The shell renders these between
 * FAQ → RelatedTools and between RelatedTools → CTA on every tool page.
 * Derived from toolName via deriveFamily(), so every one of the 90 tools gets
 * its pair without per-page wiring.
 */
// NOTE: Three Supabase collections are unpopulated as of 2026-04-27:
//   beverage-center, undercounter-refrigerator, energy-star
// The map below avoids those collections entirely. Update once they're filled.
const BONUS_CAROUSELS: Record<TopicFamily, BonusCarouselConfig> = {
  fridge: {
    primary: { eyebrow: "Trending this week", title: "Most-shopped refrigerators", description: "Regularly updated bestsellers across French door, side-by-side, and counter-depth.", collectionType: "french-door", fallbackCollectionType: "counter-depth" },
    secondary: { eyebrow: "Best value", title: "Counter-depth picks with strong efficiency ratings", description: "Counter-depth profiles consistently lead the federal-standard efficiency band.", collectionType: "counter-depth", fallbackCollectionType: "top-freezer" },
  },
  freezer: {
    primary: { eyebrow: "Top of the line", title: "Best-selling upright freezers", description: "Easiest layout for daily-access frozen storage.", collectionType: "upright-freezer", fallbackCollectionType: "bottom-freezer" },
    secondary: { eyebrow: "Bulk storage", title: "Chest freezers built for the long haul", description: "Lowest cost per cubic foot and steadiest temperatures for long-term storage.", collectionType: "chest-freezer", fallbackCollectionType: "upright-freezer" },
  },
  wine: {
    primary: { eyebrow: "For the cellar", title: "Top-rated wine refrigerators", description: "Single and dual-zone units sized for serious collections.", collectionType: "wine-cooler", fallbackCollectionType: "counter-depth" },
    secondary: { eyebrow: "If wine alone won't cut it", title: "Side-by-side fridges with through-door dispensers", description: "When the household needs water + ice + fridge alongside the cellar.", collectionType: "side-by-side", fallbackCollectionType: "french-door" },
  },
  beverage: {
    primary: { eyebrow: "Counter-depth", title: "Counter-depth fridges for entertaining", description: "Slim built-in profile with adjustable shelving for bottles and cans.", collectionType: "counter-depth", fallbackCollectionType: "side-by-side" },
    secondary: { eyebrow: "Side-by-side", title: "Side-by-side fridges with through-door dispensers", description: "Two narrow doors + dispenser — high-volume layout for hosting.", collectionType: "side-by-side", fallbackCollectionType: "french-door" },
  },
  ice: {
    primary: { eyebrow: "Best in class", title: "Fridges with the best built-in ice makers", description: "French door units that double as your ice machine — nugget, cube, and crushed.", collectionType: "french-door", fallbackCollectionType: "side-by-side" },
    secondary: { eyebrow: "Bottom-freezer pick", title: "Bottom-freezer fridges with built-in ice", description: "Quieter compressors and steadier ice production than top-freezer competitors.", collectionType: "bottom-freezer", fallbackCollectionType: "french-door" },
  },
  mini: {
    primary: { eyebrow: "Compact pick", title: "Top-freezer compact fridges", description: "The simplest layout for dorms, offices, and second fridges.", collectionType: "top-freezer", fallbackCollectionType: "counter-depth" },
    secondary: { eyebrow: "Counter-depth", title: "Counter-depth fridges that fit tight spaces", description: "When you need full features but every inch of clearance counts.", collectionType: "counter-depth", fallbackCollectionType: "bottom-freezer" },
  },
  rv: {
    primary: { eyebrow: "On the road", title: "Counter-depth fridges that travel", description: "Slimmer profile + better seal-test ratings — built for movement.", collectionType: "counter-depth", fallbackCollectionType: "top-freezer" },
    secondary: { eyebrow: "Compact alternative", title: "Top-freezer compact picks", description: "The simplest mechanical layout — fewer points of failure when you're miles from a tech.", collectionType: "top-freezer", fallbackCollectionType: "bottom-freezer" },
  },
  outdoor: {
    primary: { eyebrow: "Outdoor-rated", title: "Outdoor & garage-rated refrigerators", description: "Compressors specced for ambient swings — won't mis-cycle in summer heat or winter cold.", collectionType: "bottom-freezer", fallbackCollectionType: "counter-depth" },
    secondary: { eyebrow: "Counter-depth fit", title: "Counter-depth picks for tight spaces", description: "Slim built-in profile for outdoor kitchens and bar setups.", collectionType: "counter-depth", fallbackCollectionType: "french-door" },
  },
  commercial: {
    primary: { eyebrow: "High-volume", title: "Side-by-side fridges for heavy use", description: "Two narrow doors mean shorter open cycles — better for shared kitchens.", collectionType: "side-by-side", fallbackCollectionType: "french-door" },
    secondary: { eyebrow: "Bulk storage", title: "Bottom-freezer commercial picks", description: "Lower-bay frozen storage with full-width fresh access on top.", collectionType: "bottom-freezer", fallbackCollectionType: "side-by-side" },
  },
  filter: {
    primary: { eyebrow: "Filtration", title: "Fridges with the best built-in water filtration", description: "NSF/ANSI 42 + 53 certified through-door dispensers as standard.", collectionType: "french-door", fallbackCollectionType: "side-by-side" },
    secondary: { eyebrow: "Side-by-side", title: "Through-door dispenser picks", description: "Two narrow doors + dispenser — the highest-volume layout for filtered water + ice.", collectionType: "side-by-side", fallbackCollectionType: "counter-depth" },
  },
  "kitchen-helper": {
    primary: { eyebrow: "Trending this week", title: "Most-shopped refrigerators", description: "Regularly updated bestsellers across every fridge style on Fridge.com.", collectionType: "french-door", fallbackCollectionType: "counter-depth" },
    secondary: { eyebrow: "Best value", title: "Counter-depth picks with strong efficiency ratings", description: "Counter-depth profiles consistently lead the federal-standard efficiency band.", collectionType: "counter-depth", fallbackCollectionType: "top-freezer" },
  },
}

/**
 * Derive a topic family from the tool's name. Falls back to "fridge" —
 * the safest universal default. Designed to be wrong about ~5% of edge
 * cases rather than require per-page wiring on all 90 tools.
 */
function deriveFamily(toolName?: string): TopicFamily {
  if (!toolName) return "fridge"
  const n = toolName.toLowerCase()
  if (/\bwine\b/.test(n)) return "wine"
  if (/\bkegerator\b|\bbeer\b|\bbeverage\b/.test(n)) return "beverage"
  if (/\bice\s*maker\b/.test(n)) return "ice"
  if (/\bmini\s*fridge\b|\bdorm\b/.test(n)) return "mini"
  if (/\brv\b/.test(n)) return "rv"
  if (/\boutdoor\b|\bgarage\b/.test(n)) return "outdoor"
  if (/\bwalk-?in\b|\bcommercial\b|\bdisplay\s*cooler\b/.test(n)) return "commercial"
  if (/\bfilter\b/.test(n)) return "filter"
  if (/\bcooking\b|\brecipe\b|\bkitchen\s*timer\b|\btemperature\s*converter\b/.test(n)) return "kitchen-helper"
  if (/\bfreezer\b/.test(n) && !/\brefrigerator\b/.test(n)) return "freezer"
  return "fridge"
}

export interface ToolPageShellProps {
  /**
   * The canonical tool name — the page H1 (the editorial display text is
   * demoted to H2) AND the breadcrumb leaf: the shell builds the page's ONE
   * trail, `Fridge.com / Tools / {toolName}`, with lib/breadcrumbs.ts
   * toolBreadcrumbs() and renders it as BOTH the visible hero breadcrumb and
   * the BreadcrumbList JSON-LD (FRIDGE-269). Every /tools page passes it.
   */
  toolName?: string
  /**
   * An explicit trail from lib/breadcrumbs.ts for a shell page that is NOT a
   * tool (e.g. /widgets → staticPageBreadcrumbs). Wins over the toolName-
   * derived trail; rendered on both surfaces the same way.
   */
  trail?: BreadcrumbTrail
  /** Hero orange eyebrow (e.g. "Decision tool · Specialty") */
  heroEyebrow: string
  /** Hero H1 — pre-italic text */
  heroTitlePre: ReactNode
  /** Hero H1 — italic orange accent word */
  heroTitleItalic: ReactNode
  /** Hero H1 — optional text after the italic */
  heroTitlePost?: ReactNode
  /** Hero subtitle */
  heroSubtitle?: ReactNode
  /** Hero meta row strings */
  heroMeta?: string[]
  /** Optional share bar rendered just under the hero meta row */
  heroShare?: ReactNode
  /** Optional hero right-slot (companion card / panel) */
  heroRight?: ReactNode

  /** The main variant body — calculator, quiz, comparison, guide, etc. */
  children: ReactNode

  /** FAQ items rendered after the body */
  faqs?: FAQItem[]
  /** Optional FAQ section eyebrow (default: "Questions") */
  faqEyebrow?: string
  /** Optional FAQ section title (default: "Frequently asked") */
  faqTitle?: string

  /** Related tools rendered below the FAQ */
  relatedTools?: RelatedToolItem[]

  /**
   * Optional explicit override for the topic family used to pick the two
   * bonus carousels (rendered between FAQ→Related and Related→CTA). If
   * omitted, the family is inferred from `toolName`. Set this when the
   * tool name doesn't obviously match its actual product family
   * (e.g., a "Carbon Footprint Calculator" that's really about freezers).
   */
  topicFamily?: TopicFamily

  /** CTA band rendered at the bottom of the page */
  cta?: {
    eyebrow?: string
    title: string
    body: string
    buttonLabel: string
    buttonHref: string
  }
}

/**
 * ToolPageShell — the standard wrapper for every redesigned tool page.
 * Composes the editorial chrome (SiteHeader · EditorialHero · main · FAQ
 * section · RelatedToolsRail · CTABand · SiteFooter) around a single
 * variant-specific body slot.
 *
 * This is the leaner successor to `ToolPageTemplate` for tool pages that
 * follow the /tools redesign language. Individual variant pages (A–E) all
 * sit inside this shell and render their variant-specific sections as
 * `children`.
 */
export function ToolPageShell({
  toolName,
  trail: explicitTrail,
  heroEyebrow,
  heroTitlePre,
  heroTitleItalic,
  heroTitlePost,
  heroSubtitle,
  heroMeta,
  heroShare,
  heroRight,
  children,
  faqs,
  faqEyebrow = "Questions",
  faqTitle = "Frequently asked",
  relatedTools,
  topicFamily,
  cta,
}: ToolPageShellProps) {
  const bonus = BONUS_CAROUSELS[topicFamily ?? deriveFamily(toolName)]
  // ONE trail — Fridge.com / Tools / {toolName} (or the explicit trail a
  // non-tool shell page passes) — rendered visibly in the hero and emitted as
  // the BreadcrumbList below from this same array. (Until FRIDGE-269 each
  // page passed a shortened visible leaf such as "Age Calculator" while the
  // layout-level JSON-LD said "Free Tools / Refrigerator Age Calculator".)
  const trail: BreadcrumbTrail = explicitTrail ?? (toolName ? toolBreadcrumbs({ name: toolName }) : [])
  return (
    <>
      <BreadcrumbJsonLd trail={trail} id="tool-breadcrumb-jsonld" />
      <SiteHeader />

      <EditorialHero
        trail={trail}
        toolName={toolName}
        eyebrow={heroEyebrow}
        titlePre={heroTitlePre}
        titleItalic={heroTitleItalic}
        titlePost={heroTitlePost}
        subtitle={heroSubtitle}
        meta={heroMeta}
        share={heroShare}
        right={heroRight}
      />

      {children}

      {faqs && faqs.length > 0 && (
        <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
          <div className="max-w-[1120px] mx-auto px-6 md:px-8">
            <SectionRule eyebrow={faqEyebrow} title={faqTitle} />
            <FAQAccordion
              items={faqs}
              columns={1}
              defaultOpen={[0]}
              title=""
              bordered={false}
            />
          </div>
        </section>
      )}

      {/* Bonus carousel #1 — placed after FAQ so it's not adjacent to the
          variant body's own carousels. Topic-family-derived from toolName.
          randomize=true so we don't render identical products to a body
          carousel that may use the same collection. */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow={bonus.primary.eyebrow}
          title={bonus.primary.title}
          description={bonus.primary.description}
          collectionType={bonus.primary.collectionType}
          fallbackCollectionType={bonus.primary.fallbackCollectionType}
          randomize
        />
      </section>

      {relatedTools && relatedTools.length > 0 && <RelatedToolsRail tools={relatedTools} />}

      {/* Bonus carousel #2 — between RelatedTools and CTA. */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow={bonus.secondary.eyebrow}
          title={bonus.secondary.title}
          description={bonus.secondary.description}
          collectionType={bonus.secondary.collectionType}
          fallbackCollectionType={bonus.secondary.fallbackCollectionType}
          randomize
        />
      </section>

      {cta && (
        <CTABand
          eyebrow={cta.eyebrow}
          title={cta.title}
          body={cta.body}
          buttonLabel={cta.buttonLabel}
          buttonHref={cta.buttonHref}
        />
      )}

      <ToolRecentlyViewedRail />

      <SiteFooter />
    </>
  )
}
